import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/Toast";
import { useContextMenu } from "../../components/Menu";
import { msg } from "./manageUtils";

export const idOf = (m) => m.user?.id || m.id;

/** Members tab data + actions (AdminScreen :726, :2157-2322, components/admin/MemberCard.js:86-89). */
export default function useManageMembers({ club, onTransferred }) {
  const { user } = useAuth();
  const toast = useToast();
  const { menu, openMenu } = useContextMenu();
  const clubId = club.id;
  const viewerIsAdmin = club.member_role === "admin";

  const [state, setState] = useState({ loading: true, error: null });
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { kind: "promote"|"demote"|"transfer"|"remove", member }
  const [reload, setReload] = useState(0);
  const membersRef = useRef(members);
  const clubRef = useRef(clubId);
  membersRef.current = members;
  clubRef.current = clubId;

  useEffect(() => {
    let live = true;
    setState({ loading: true, error: null });
    ClubService.getClubMembers(clubId).then(({ data, error }) => {
      if (!live) return;
      if (error) { setState({ loading: false, error }); return; }
      setMembers(data || []);
      setState({ loading: false, error: null });
    });
    return () => { live = false; };
  }, [clubId, reload]);

  const setBusyId = useCallback((ids, on) => {
    setBusy((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);
  const isBusy = (id) => busy.has(id);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      [m.user?.full_name, m.user?.email, m.user?.major].some((v) => typeof v === "string" && v.toLowerCase().includes(q))
    );
  }, [members, search]);
  const leaders = shown.filter((m) => m.role === "admin" || m.role === "e-board");
  const plain = shown.filter((m) => m.role !== "admin" && m.role !== "e-board");

  const changeRole = async (member, newRole) => {
    const id = idOf(member);
    if (busy.has(id)) return;
    const at = clubId;
    const previous = member.role;
    setBusyId([id], true);
    setMembers((prev) => prev.map((m) => (idOf(m) === id ? { ...m, role: newRole } : m)));
    try {
      const { error } = await ClubService.updateMemberRole(at, id, newRole);
      if (error) {
        if (clubRef.current === at) {
          // Only revert if this call's value is still what the row shows (a newer toggle must win).
          setMembers((prev) => prev.map((m) => (idOf(m) === id && m.role === newRole ? { ...m, role: previous } : m)));
        }
        toast(msg(error, "Failed to update role"), "alert");
      }
    } finally {
      setBusyId([id], false);
    }
  };

  const transfer = async (member) => {
    const id = idOf(member);
    const me = user.uid;
    if (busy.has(id) || busy.has(me)) return;
    const at = clubId;
    const targetPrev = member.role;
    setBusyId([id, me], true);
    setMembers((prev) => prev.map((m) => (idOf(m) === me ? { ...m, role: "member" } : idOf(m) === id ? { ...m, role: "admin" } : m)));
    try {
      const { error } = await ClubService.transferAdminControl(at, me, id);
      if (error) {
        setMembers((prev) => prev.map((m) => (idOf(m) === me ? { ...m, role: "admin" } : idOf(m) === id ? { ...m, role: targetPrev } : m)));
        toast(msg(error, "Failed to transfer admin control"), "alert");
        return;
      }
      toast("Admin control transferred");
      await onTransferred(at);
    } finally {
      setBusyId([id, me], false);
    }
  };

  const remove = async (member) => {
    const id = idOf(member);
    if (busy.has(id)) return;
    const at = clubId;
    setBusyId([id], true);
    setMembers((prev) => prev.filter((m) => idOf(m) !== id));
    try {
      const { error } = await ClubService.leaveClub(at, id);
      if (error) {
        if (clubRef.current === at) setMembers((prev) => (prev.some((m) => idOf(m) === id) ? prev : [...prev, member]));
        toast(msg(error, "Failed to remove member"), "alert");
      }
    } finally {
      setBusyId([id], false);
    }
  };

  const run = () => {
    const { kind, member } = confirm;
    setConfirm(null);
    // Re-read the row: a stale menu must not reinterpret a role that changed since (AdminScreen resolveCurrentTarget).
    const current = membersRef.current.find((m) => idOf(m) === idOf(member));
    if (!current) { toast("That member is no longer in the club.", "alert"); return; }
    if (kind === "remove") return remove(current);
    if (current.role === "admin" || (kind !== "promote") === (current.role !== "e-board")) {
      toast(`${current.user?.full_name || "This member"}'s role has changed. Please try again.`, "alert");
      return;
    }
    if (kind === "transfer") return transfer(current);
    return changeRole(current, kind === "promote" ? "e-board" : "member");
  };

  const manageMenu = (e, member) => {
    const r = e.currentTarget.getBoundingClientRect();
    const items = member.role === "e-board"
      ? [
          { icon: "key", label: "Transfer Admin", danger: true, testId: "manage.members.menu.transfer", run: () => setConfirm({ kind: "transfer", member }) },
          { icon: "user", label: "Demote to Member", testId: "manage.members.menu.demote", run: () => setConfirm({ kind: "demote", member }) },
        ]
      : [{ icon: "star", label: "Promote to E-Board", testId: "manage.members.menu.promote", run: () => setConfirm({ kind: "promote", member }) }];
    openMenu(r.right - 190, r.bottom + 6, items);
  };

  return {
    user, menu, viewerIsAdmin, state, members, search, setSearch, confirm, setConfirm,
    isBusy, shown, leaders, plain, run, manageMenu, retry: () => setReload((n) => n + 1),
  };
}
